import React from "react";
import { View, StyleSheet } from "react-native";
import FileList from "../components/file-list";
import { ItemCount } from "../components/item-count";
import { useLocation, LocationStatus } from "../contexts/location";
import { useQuery } from "react-query";
import { getFiles } from "../util/api";
import { FileModel } from "../data";
import { ProgressBar } from "react-native-paper";
import WarningScreen from "./Warning";

export default function List() {
  const { locationState } = useLocation();
  const { data: files, isLoading, isError } = useQuery<FileModel[], Error>(
    "files",
    locationState.status == LocationStatus.LOCATION_READY
      ? () => getFiles(locationState.location)
      : null,
    { 
      initialData: [],
      refetchInterval: 30000, // Auto-refresh every 30 seconds
      refetchOnWindowFocus: true, // Refresh when user returns to tab
    }
  );

  // Filter files to only show those within 1km radius
  const nearbyFiles = React.useMemo(() => {
    if (!files || locationState.status !== LocationStatus.LOCATION_READY) {
      return [];
    }

    const userLat = locationState.location.coords.latitude;
    const userLon = locationState.location.coords.longitude;

    return files.filter((file) => {
      // Calculate distance from user location using Haversine formula
      const R = 6371e3; // Earth radius in meters
      const φ1 = (userLat * Math.PI) / 180;
      const φ2 = (file.lat * Math.PI) / 180;
      const Δφ = ((file.lat - userLat) * Math.PI) / 180;
      const Δλ = ((file.lon - userLon) * Math.PI) / 180;

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c; // Distance in meters

      return distance <= 2000; // Only show files within 2km
    });
  }, [files, locationState]);

  if (isLoading) {
    return <ProgressBar progress={0.8} />;
  }

  if (isError) {
    return <WarningScreen message="Sorry we couldn't load this right now :(" />;
  }

  return (
    <View style={styles.contentContainer}>
      <FileList files={nearbyFiles} />
      <ItemCount itemName="Files Nearby" count={nearbyFiles.length} />
    </View>
  );
}
const styles = StyleSheet.create({
  contentContainer: {
    flexGrow: 1,
    width: "100%",
    maxWidth: 760,
    marginLeft: "auto",
    marginRight: "auto",
  },
});
